import axios from "axios";
import {
  TP_BATCH_SIZE,
  TP_FETCH_CONCURRENCY,
  TP_CACHE_FRESH_MS,
  TP_CACHE_STALE_MS,
  TP_CACHE_PERSIST_MAX_AGE_MS,
  TP_CACHE_STORAGE_KEY,
  TP_CACHE_MAX_ENTRIES,
  TP_REFRESH_INTERVAL_MS,
  TP_HOT_ID_WINDOW_MS,
} from "../config/cachePolicy.js";

const API_BASE_URL = import.meta.env.DEV
  ? "/api/gw2"
  : "https://api.guildwars2.com";
const USE_SERVER_TP_PROXY = true;
const TP_PROXY_ENDPOINT = "/api/tp-prices";
const USE_SERVER_ITEMS_PROXY = true;
const ITEMS_PROXY_ENDPOINT = "/api/items";

function chunkArray(items, size) {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

class GW2API {
  constructor() {
    this.apiKey = null;
    this.language = "en"; // Default to English
    this.axios = axios.create({
      baseURL: API_BASE_URL,
      timeout: 45000,
    });

    this.tpPriceCache = new Map();
    this.tpPriceLastRequestedAt = new Map();
    this.tpBatchInFlight = new Map();
    this.tpCachePersistTimeout = null;
    this.loadTpPriceCacheFromStorage();

    if (typeof window !== "undefined") {
      this.tpRefreshTimer = window.setInterval(() => {
        this.refreshHotTpPrices().catch(() => {});
      }, TP_REFRESH_INTERVAL_MS);
    }
  }

  loadTpPriceCacheFromStorage() {
    if (typeof window === "undefined") return;

    try {
      const raw = window.localStorage.getItem(TP_CACHE_STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return;

      const now = Date.now();
      Object.entries(parsed).forEach(([id, entry]) => {
        const numericId = Number(id);
        if (!Number.isFinite(numericId)) return;

        const updatedAt = Number(entry?.updatedAt);
        if (!Number.isFinite(updatedAt)) return;
        if (now - updatedAt > TP_CACHE_PERSIST_MAX_AGE_MS) return;

        this.tpPriceCache.set(numericId, {
          updatedAt,
          price: entry?.price ?? null,
        });
      });
    } catch {
      // Ignore malformed storage
    }
  }

  schedulePersistTpPriceCache() {
    if (typeof window === "undefined") return;
    if (this.tpCachePersistTimeout) return;

    this.tpCachePersistTimeout = window.setTimeout(() => {
      this.tpCachePersistTimeout = null;

      try {
        const entries = [...this.tpPriceCache.entries()]
          .sort((a, b) => b[1].updatedAt - a[1].updatedAt)
          .slice(0, TP_CACHE_MAX_ENTRIES);

        const serialized = {};
        entries.forEach(([id, entry]) => {
          serialized[id] = {
            updatedAt: entry.updatedAt,
            price: entry.price,
          };
        });

        window.localStorage.setItem(
          TP_CACHE_STORAGE_KEY,
          JSON.stringify(serialized),
        );
      } catch {
        // Ignore storage failures
      }
    }, 500);
  }

  updateTpCache(priceMap, updatedAt = Date.now()) {
    Object.entries(priceMap).forEach(([id, price]) => {
      const numericId = Number(id);
      if (!Number.isFinite(numericId)) return;
      this.tpPriceCache.set(numericId, { updatedAt, price: price ?? null });
    });

    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("tp-cache-updated"));
    }

    this.schedulePersistTpPriceCache();
  }

  getTpPriceCacheStatus() {
    const entries = [...this.tpPriceCache.values()];
    if (!entries.length) {
      return {
        hasData: false,
        totalEntries: 0,
        lastUpdatedAt: null,
        freshestAgeMs: null,
      };
    }

    const now = Date.now();
    let lastUpdatedAt = 0;

    entries.forEach((entry) => {
      if (entry.updatedAt > lastUpdatedAt) {
        lastUpdatedAt = entry.updatedAt;
      }
    });

    return {
      hasData: true,
      totalEntries: entries.length,
      lastUpdatedAt,
      freshestAgeMs: Math.max(0, now - lastUpdatedAt),
    };
  }

  getTpCacheState(id, nowTs = Date.now()) {
    const entry = this.tpPriceCache.get(id);
    if (!entry) return { state: "missing", price: null };

    const age = nowTs - entry.updatedAt;
    if (age <= TP_CACHE_FRESH_MS) {
      return { state: "fresh", price: entry.price };
    }
    if (age <= TP_CACHE_STALE_MS) {
      return { state: "stale", price: entry.price };
    }
    return { state: "expired", price: entry.price };
  }

  async fetchTpPriceBatch(batchIds) {
    if (batchIds.length === 0) return {};

    const key = batchIds.join(",");
    const existing = this.tpBatchInFlight.get(key);
    if (existing) {
      return existing;
    }

    const requestPromise = (async () => {
      let rows = null;

      if (USE_SERVER_TP_PROXY) {
        try {
          rows = await this.requestTpPricesFromBackend(batchIds);
        } catch {
          rows = null;
        }
      }

      const map = {};
      if (Array.isArray(rows)) {
        rows.forEach((row) => {
          if (row?.id) {
            map[row.id] = row;
          }
        });
      }

      batchIds.forEach((id) => {
        if (map[id] === undefined) {
          map[id] = null;
        }
      });

      return map;
    })()
      .catch(async (error) => {
        console.error("Failed to fetch TP prices:", error);
        const fallback = {};
        batchIds.forEach((id) => {
          const cached = this.tpPriceCache.get(id);
          fallback[id] = cached ? cached.price : null;
        });
        return fallback;
      })
      .finally(() => {
        this.tpBatchInFlight.delete(key);
      });

    this.tpBatchInFlight.set(key, requestPromise);
    return requestPromise;
  }

  async fetchTpPricesByBatches(ids) {
    const batches = chunkArray(ids, TP_BATCH_SIZE);
    const merged = {};

    for (let i = 0; i < batches.length; i += TP_FETCH_CONCURRENCY) {
      const group = batches.slice(i, i + TP_FETCH_CONCURRENCY);
      const groupResults = await Promise.all(
        group.map((batch) =>
          this.fetchTpPriceBatch(batch).catch(() => {
            const fallback = {};
            batch.forEach((id) => {
              const cached = this.tpPriceCache.get(id);
              fallback[id] = cached ? cached.price : null;
            });
            return fallback;
          }),
        ),
      );

      groupResults.forEach((resultMap) => {
        Object.assign(merged, resultMap);
      });
    }

    return merged;
  }

  async refreshHotTpPrices() {
    const now = Date.now();
    const hotIds = [];

    this.tpPriceLastRequestedAt.forEach((lastRequestedAt, id) => {
      if (now - lastRequestedAt <= TP_HOT_ID_WINDOW_MS) {
        hotIds.push(id);
      }
    });

    if (!hotIds.length) return;

    const fetchedMap = await this.fetchTpPricesByBatches(hotIds);
    this.updateTpCache(fetchedMap, now);
  }

  async requestTpPricesFromBackend(batchIds) {
    if (typeof window === "undefined") return null;

    const params = new URLSearchParams();
    params.set("ids", batchIds.join(","));
    const response = await fetch(`${TP_PROXY_ENDPOINT}?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`TP backend responded ${response.status}`);
    }

    const payload = await response.json();
    if (Array.isArray(payload)) {
      return payload;
    }

    return Array.isArray(payload?.prices) ? payload.prices : [];
  }

  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  isRetryableError(error) {
    if (!error) return false;
    const status = error.response?.status;
    const code = error.code;

    if (code === "ECONNABORTED" || code === "ERR_NETWORK") {
      return true;
    }

    return status >= 500;
  }

  async requestWithRetry(config, retries = 2, initialDelayMs = 400) {
    let attempt = 0;
    let delay = initialDelayMs;

    while (attempt <= retries) {
      try {
        const response = await this.axios.request(config);
        return response;
      } catch (error) {
        const shouldRetry = attempt < retries && this.isRetryableError(error);
        if (!shouldRetry) {
          throw error;
        }
        await this.sleep(delay);
        delay *= 2;
        attempt += 1;
      }
    }
  }

  setApiKey(key) {
    this.apiKey = key;
  }

  setLanguage(lang) {
    this.language = lang;
  }

  // Use query params instead of headers to avoid CORS preflight
  // GW2 API docs: "As most browsers prevent CORS, the request header method
  // will only be possible via custom applications. If using a browser, use a query parameter."
  getAuthParams() {
    const params = this.apiKey ? { access_token: this.apiKey } : {};
    // Add language parameter for localized content
    if (this.language) {
      params.lang = this.language;
    }
    return params;
  }

  // Account endpoints
  async getAccount() {
    const response = await this.requestWithRetry({
      method: "get",
      url: "/v2/account",
      params: this.getAuthParams(),
    });
    return response.data;
  }

  async getBank() {
    const response = await this.requestWithRetry({
      method: "get",
      url: "/v2/account/bank",
      params: this.getAuthParams(),
    });
    return response.data;
  }

  async getMaterials() {
    const response = await this.requestWithRetry({
      method: "get",
      url: "/v2/account/materials",
      params: this.getAuthParams(),
    });
    return response.data;
  }

  async getInventory() {
    const response = await this.requestWithRetry({
      method: "get",
      url: "/v2/account/inventory",
      params: this.getAuthParams(),
    });
    return response.data;
  }

  async getCharacters() {
    const response = await this.requestWithRetry({
      method: "get",
      url: "/v2/characters",
      params: this.getAuthParams(),
    });
    return response.data;
  }

  async getCharacterInventory(characterName) {
    const response = await this.requestWithRetry({
      method: "get",
      url: `/v2/characters/${encodeURIComponent(characterName)}/inventory`,
      params: this.getAuthParams(),
    });
    return response.data;
  }

  async getCharacterEquipment(characterName) {
    const response = await this.requestWithRetry({
      method: "get",
      url: `/v2/characters/${encodeURIComponent(characterName)}/equipment`,
      params: this.getAuthParams(),
    });
    return response.data;
  }

  async getWallet() {
    const response = await this.requestWithRetry({
      method: "get",
      url: "/v2/account/wallet",
      params: this.getAuthParams(),
    });
    return response.data;
  }

  // Item endpoints
  async getItems(ids) {
    if (USE_SERVER_ITEMS_PROXY && !import.meta.env.DEV) {
      const idsParam = Array.isArray(ids) ? ids.join(",") : ids;
      const params = new URLSearchParams();
      params.set("ids", idsParam);
      params.set("lang", this.language);
      const response = await fetch(
        `${ITEMS_PROXY_ENDPOINT}?${params.toString()}`,
      );
      const data = await response.json();
      return data.items || [];
    }

    const idsParam = Array.isArray(ids) ? ids.join(",") : ids;
    const response = await this.axios.get(`/v2/items?ids=${idsParam}`, {
      params: { lang: this.language },
    });
    return response.data;
  }

  async getItemStats(ids) {
    const idsParam = Array.isArray(ids) ? ids.join(",") : ids;
    const response = await this.axios.get(`/v2/itemstats?ids=${idsParam}`);
    return response.data;
  }

  async getSkins(ids) {
    const idsParam = Array.isArray(ids) ? ids.join(",") : ids;
    const response = await this.axios.get(`/v2/skins?ids=${idsParam}`);
    return response.data;
  }

  // Currency endpoints
  async getCurrencies(ids) {
    const idsParam = Array.isArray(ids) ? ids.join(",") : ids;
    const response = await this.axios.get(`/v2/currencies?ids=${idsParam}`, {
      params: { lang: this.language },
    });
    return response.data;
  }

  // Trading Post endpoints
  async getPrices(ids) {
    const normalizedIds = [
      ...new Set(
        (Array.isArray(ids) ? ids : [ids])
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id)),
      ),
    ];

    if (!normalizedIds.length) return [];

    const now = Date.now();
    normalizedIds.forEach((id) => {
      this.tpPriceLastRequestedAt.set(id, now);
    });

    const resultMap = {};
    const idsToFetch = [];

    normalizedIds.forEach((id) => {
      const cacheState = this.getTpCacheState(id, now);

      if (cacheState.state === "fresh") {
        resultMap[id] = cacheState.price;
        return;
      }

      if (cacheState.state === "stale") {
        resultMap[id] = cacheState.price;
        idsToFetch.push(id);
        return;
      }

      idsToFetch.push(id);
    });

    if (idsToFetch.length) {
      const fetchedMap = await this.fetchTpPricesByBatches(idsToFetch);
      this.updateTpCache(fetchedMap, now);
      Object.assign(resultMap, fetchedMap);
    }

    return normalizedIds
      .map((id) => resultMap[id])
      .filter((entry) => entry && entry.id);
  }

  async getListings(ids) {
    const idsParam = Array.isArray(ids) ? ids.join(",") : ids;
    const response = await this.axios.get(
      `/v2/commerce/listings?ids=${idsParam}`,
    );
    return response.data;
  }

  // Recipe endpoints
  async getRecipes(ids) {
    const idsParam = Array.isArray(ids) ? ids.join(",") : ids;
    const response = await this.axios.get(`/v2/recipes?ids=${idsParam}`, {
      params: { lang: this.language },
    });
    return response.data;
  }

  async searchRecipesByOutput(itemId) {
    const response = await this.axios.get(
      `/v2/recipes/search?output=${itemId}`,
    );
    return response.data;
  }

  async searchRecipesByInput(itemId) {
    const response = await this.axios.get(`/v2/recipes/search?input=${itemId}`);
    return response.data;
  }

  // Validate API key
  async validateApiKey() {
    try {
      const response = await this.axios.get("/v2/tokeninfo", {
        params: this.getAuthParams(),
      });
      return response.data;
    } catch (error) {
      throw new Error("Invalid API key");
    }
  }

  // Get item icon URL - API already provides full URL
  getItemIconUrl(iconUrl, itemId) {
    if (!iconUrl) {
      // Fallback to default item icon if no URL
      return `https://render.guildwars2.com/file/220061640ECA41C0577758030357221B4ECCE62C/42684.png`;
    }
    // API provides complete URL, just return it
    return iconUrl;
  }

  // Get wiki item URL as fallback
  getWikiItemUrl(itemId) {
    return `https://api.guildwars2.com/v2/items/${itemId}`;
  }
}

export default new GW2API();
